import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '../ui/Button';
import styles from './ProductLandingPage.module.css';

export interface ProductLandingPageContent {
  hero: {
    eyebrow: string;
    title: string;
    subtitle: string;
    cta: { label: string; href: string };
  };
  problem: {
    title: string;
    items: string[];
  };
  solution: {
    title: string;
    items: { title: string; desc: string }[];
  };
  steps: {
    title: string;
    items: { title: string; desc: string }[];
  };
  cta: {
    title: string;
    subtitle: string;
    button: { label: string; href: string };
  };
}

export const ProductLandingPage: React.FC<{ content: ProductLandingPageContent }> = ({ content }) => (
  <div className={styles.page}>
    <section className={styles.hero}>
      <div className={styles.container}>
        <span className={styles.eyebrow}>{content.hero.eyebrow}</span>
        <h1 className={styles.title}>{content.hero.title}</h1>
        <p className={styles.subtitle}>{content.hero.subtitle}</p>
        <div className={styles.actions}>
          <Button variant="primary" size="lg" icon={<ArrowRight size={20} />} to={content.hero.cta.href}>
            {content.hero.cta.label}
          </Button>
        </div>
      </div>
    </section>

    <section className={styles.section}>
      <div className={styles.container}>
        <h2 className={styles.sectionTitle}>{content.problem.title}</h2>
        <ul className={styles.bulletList}>
          {content.problem.items.map((item) => (
            <li key={item} className={styles.bulletItem}>
              <span className={styles.bulletDot} />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section className={styles.sectionAlt}>
      <div className={styles.container}>
        <h2 className={styles.sectionTitle}>{content.solution.title}</h2>
        <div className={styles.cardGrid}>
          {content.solution.items.map((item) => (
            <div key={item.title} className={styles.card}>
              <h3 className={styles.cardTitle}>{item.title}</h3>
              <p className={styles.cardDesc}>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className={styles.section}>
      <div className={styles.container}>
        <h2 className={styles.sectionTitle}>{content.steps.title}</h2>
        <div className={styles.stepGrid}>
          {content.steps.items.map((item, index) => (
            <div key={item.title} className={styles.stepCard}>
              <div className={styles.stepIndex}>{`0${index + 1}`}</div>
              <div>
                <h3 className={styles.stepTitle}>{item.title}</h3>
                <p className={styles.stepDesc}>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className={styles.ctaSection}>
      <div className={styles.container}>
        <div className={styles.ctaCard}>
          <div>
            <h2 className={styles.ctaTitle}>{content.cta.title}</h2>
            <p className={styles.ctaSubtitle}>{content.cta.subtitle}</p>
          </div>
          <Button variant="primary" size="lg" icon={<ArrowRight size={20} />} to={content.cta.button.href}>
            {content.cta.button.label}
          </Button>
        </div>
      </div>
    </section>
  </div>
);

export default ProductLandingPage;
